import React, { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import MusicVisualizer from "../components/MusicVisualizer/MusicVisualizer";
import { contentBoxStyle, imageContainerStyle } from "../styles/commonStyles";
import colors from "../styles/colors";
import StyledButton from "../components/StyledButton/StyledButton";

const AudioComposition: React.FC = () => {
  const [currentAudioFile, setCurrentAudioFile] = useState(
    "/audio/DieRoutine.mp3"
  );
  const [isPlaying, setIsPlaying] = useState(false);

  const handleAudioChange = (file: string) => {
    setCurrentAudioFile(file);
    setIsPlaying(false); // Reset play state when changing files
  };

  return (
    <Box id="content" sx={contentBoxStyle}>
      <Box
        id="text-container"
        sx={{
          maxWidth: "1564px",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // 1 column on extra small screens
            sm: "1fr 1fr", // 2 columns on small screens (1:1 ratio)
            md: "2fr 2fr", // 3 columns on medium screens (1:2 ratio)
          },
          gap: "2rem",
          paddingTop: "10rem",
          paddingBottom: "5rem",
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "white",
            wordBreak: "break-word",
            borderRadius: "0px 0px 0px 0px",
            boxShadow: "2px 2px 4px rgba(177, 142, 91, 0.5)",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: colors.filterColors[3],
              margin: "1rem",
            }}
          >
            Audio Composition
          </Typography>
        </Box>
        <Box
          sx={{
            color: "white",
            borderRadius: "50px 0px 50px 0px",
            backgroundColor: colors.filterColors[3],
          }}
        >
          <Typography variant="body1" margin="1.5rem 1.8rem">
            <p>
              My fascination with design and technical systems has always been
              at the core of my creative process, and it naturally extended into
              my music.
            </p>
            <p>
              Just as I question how things work and how they can be improved in
              design, I approach music with the same mindset — seeking to
              understand the intricacies of sound and how it can be shaped.
              Instead of using a PC, I focus on working with electronic
              instruments like samplers, synthesizers, and drum machines.
            </p>
            <p>
              For me, it's not just about making music; it's about learning
              these complex tools and pushing their boundaries. Each instrument
              has its own unique language, and by mastering them, I improve my
              ability to shape sound and create more expressive, dynamic
              compositions.
            </p>
          </Typography>
        </Box>
      </Box>
      <Box
        id="music-container"
        sx={{
          width: "100%",
          maxWidth: "1564px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "-5rem",
          paddingBottom: "10rem",
        }}
      >
        <MusicVisualizer
          width={800}
          height={500}
          audioFile={currentAudioFile}
          isPlaying={isPlaying}
          onPlayingChange={setIsPlaying}
        />
        <Box
          id="audio-buttons"
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "1rem",
            paddingTop: "5rem",
          }}
        >
          <Button
            onClick={() => handleAudioChange("/audio/DerAbendsprung.mp3")}
            variant={
              currentAudioFile === "/audio/DerAbendsprung.mp3"
                ? "contained"
                : "outlined"
            }
            sx={{
              padding: "0.5rem 1rem",
              fontSize: "1.2rem",
              fontWeight: 400,
              letterSpacing: "0.2rem",
              color:
                currentAudioFile === "/audio/DerAbendsprung.mp3"
                  ? "white"
                  : colors.filterColors[3],
              backgroundColor:
                currentAudioFile === "/audio/DerAbendsprung.mp3"
                  ? colors.filterColors[3]
                  : "white",
              border: "none",
              borderRadius: "0px",
              boxShadow:
                currentAudioFile === "/audio/DerAbendsprung.mp3"
                  ? "none"
                  : "2px 2px 4px rgba(177, 142, 91, 0.5)",
              "&:hover": {
                color: "white",
                backgroundColor: colors.filterColors[3],
                boxShadow:
                  currentAudioFile === "/audio/DerAbendsprung.mp3"
                    ? "none"
                    : "2px 2px 4px rgba(177, 142, 91, 0.5)",
              },
              "&.Mui-active, &:active": {
                color: "white",
                backgroundColor: colors.filterColors[3],
                boxShadow: "none",
              },
            }}
          >
            DerAbendsprung
          </Button>

          <Button
            onClick={() => handleAudioChange("/audio/DieFeuerwehr.mp3")}
            variant={
              currentAudioFile === "/audio/DieFeuerwehr.mp3"
                ? "contained"
                : "outlined"
            }
            sx={{
              padding: "0.5rem 1rem",
              fontSize: "1.2rem",
              fontWeight: 400,
              letterSpacing: "0.2rem",
              color:
                currentAudioFile === "/audio/DieFeuerwehr.mp3"
                  ? "white"
                  : colors.filterColors[3],
              backgroundColor:
                currentAudioFile === "/audio/DieFeuerwehr.mp3"
                  ? colors.filterColors[3]
                  : "white",
              border: "none",
              borderRadius: "0px",
              boxShadow:
                currentAudioFile === "/audio/DieFeuerwehr.mp3"
                  ? "none"
                  : "2px 2px 4px rgba(177, 142, 91, 0.5)",
              "&:hover": {
                color: "white",
                backgroundColor: colors.filterColors[3],
                boxShadow:
                  currentAudioFile === "/audio/DieFeuerwehr.mp3"
                    ? "none"
                    : "2px 2px 4px rgba(177, 142, 91, 0.5)",
              },
              "&.Mui-active, &:active": {
                color: "white",
                backgroundColor: colors.filterColors[3],
                boxShadow: "none",
              },
            }}
          >
            DieFeuerwehr
          </Button>

          <Button
            onClick={() => handleAudioChange("/audio/DieRoutine.mp3")}
            variant={
              currentAudioFile === "/audio/DieRoutine.mp3"
                ? "contained"
                : "outlined"
            }
            sx={{
              padding: "0.5rem 1rem",
              fontSize: "1.2rem",
              fontWeight: 400,
              letterSpacing: "0.2rem",
              color:
                currentAudioFile === "/audio/DieRoutine.mp3"
                  ? "white"
                  : colors.filterColors[3],
              backgroundColor:
                currentAudioFile === "/audio/DieRoutine.mp3"
                  ? colors.filterColors[3]
                  : "white",
              border: "none",
              borderRadius: "0px",
              boxShadow:
                currentAudioFile === "/audio/DieRoutine.mp3"
                  ? "none"
                  : "2px 2px 4px rgba(177, 142, 91, 0.5)",
              "&:hover": {
                color: "white",
                backgroundColor: colors.filterColors[3],
                boxShadow:
                  currentAudioFile === "/audio/DieRoutine.mp3"
                    ? "none"
                    : "2px 2px 4px rgba(177, 142, 91, 0.5)",
              },
              "&.Mui-active, &:active": {
                color: "white",
                backgroundColor: colors.filterColors[3],
                boxShadow: "none",
              },
            }}
          >
            DieRoutine
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AudioComposition;
